import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
  rectIntersection,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Copy, Trash2, GripVertical, Plus } from 'lucide-react';
import { FIELD_PALETTE } from '../../../lib/fieldPalette';
import PageContainer from './PageContainer';
import type { FormField, FormPage } from '../../../types/forms';
import { PAGE_EXCLUDED_FIELD_TYPES } from '../../../types/forms';

interface SlidePanelProps {
  fields: FormField[];
  pages: FormPage[];
  selectedId: string | null;
  onSelect: (fieldId: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onDelete: (fieldId: string) => void;
  onDuplicate: (fieldId: string) => void;
  // Page actions
  onAddPage: () => void;
  onDeletePage: (pageId: string) => void;
  onUpdatePage: (pageId: string, updates: Partial<FormPage>) => void;
  onReorderPages: (fromIndex: number, toIndex: number) => void;
  onMoveFieldToPage: (fieldId: string, pageId: string | null) => void;
  selectedPageId: string | null;
  onSelectPage: (pageId: string | null) => void;
}

// ─── dnd-kit drag/drop data (mobile-ui.4.C D3) ───────────────
// Replaces the HTML5 drag flows 1:1: field reorder (field → field-target),
// page reorder (page → page-target), move field into page (field →
// page-container) and drop back to ungrouped (field → ungrouped).

export type DragData =
  | { kind: 'field'; fieldId: string; originalIndex: number }
  | { kind: 'page'; pageIndex: number };

export type DropData =
  | { kind: 'field-target'; originalIndex: number }
  | { kind: 'page-target'; pageIndex: number }
  | { kind: 'page-container'; pageId: string }
  | { kind: 'ungrouped' };

export type DropAction =
  | { type: 'reorder-fields'; from: number; to: number }
  | { type: 'reorder-pages'; from: number; to: number }
  | { type: 'move-to-page'; fieldId: string; pageId: string | null };

/** Pure drop-resolution logic — exported for tests. */
export function resolveDragEnd(active?: DragData, over?: DropData): DropAction | null {
  if (!active || !over) return null;
  if (active.kind === 'field') {
    switch (over.kind) {
      case 'field-target':
        return active.originalIndex !== over.originalIndex
          ? { type: 'reorder-fields', from: active.originalIndex, to: over.originalIndex }
          : null;
      case 'page-container':
        return { type: 'move-to-page', fieldId: active.fieldId, pageId: over.pageId };
      case 'ungrouped':
        return { type: 'move-to-page', fieldId: active.fieldId, pageId: null };
      default:
        return null;
    }
  }
  if (active.kind === 'page' && over.kind === 'page-target') {
    return active.pageIndex !== over.pageIndex
      ? { type: 'reorder-pages', from: active.pageIndex, to: over.pageIndex }
      : null;
  }
  return null;
}

const FIELD_ACCEPTS = new Set(['field-target', 'page-container', 'ungrouped']);
const PAGE_ACCEPTS = new Set(['page-target']);

// Filters droppables by the active kind (pages only reorder against pages;
// fields never hit page-target) and prefers the precise field row over the
// surrounding page container so drop-on-row keeps meaning "reorder".
const collisionDetection: CollisionDetection = (args) => {
  const kind = (args.active.data.current as DragData | undefined)?.kind;
  const accepts = kind === 'page' ? PAGE_ACCEPTS : FIELD_ACCEPTS;
  const droppableContainers = args.droppableContainers.filter((c) => {
    const dropKind = (c.data.current as DropData | undefined)?.kind;
    return dropKind !== undefined && accepts.has(dropKind);
  });
  const hits = pointerWithin({ ...args, droppableContainers });
  if (kind === 'field' && hits.length > 1) {
    const byId = new Map(droppableContainers.map((c) => [c.id, c]));
    const fieldHit = hits.find(
      (h) => (byId.get(h.id)?.data.current as DropData | undefined)?.kind === 'field-target',
    );
    if (fieldHit) return [fieldHit];
  }
  // Keyboard drags have no pointer coordinates — fall back to rect intersection.
  return hits.length > 0 ? hits : rectIntersection({ ...args, droppableContainers });
};

// Map field type → icon component from FIELD_PALETTE
const fieldIconMap = Object.fromEntries(
  FIELD_PALETTE.map(f => [f.type, f.icon])
);

export default function SlidePanel({
  fields,
  pages,
  selectedId,
  onSelect,
  onReorder,
  onDelete,
  onDuplicate,
  onAddPage,
  onDeletePage,
  onUpdatePage,
  onReorderPages,
  onMoveFieldToPage,
  selectedPageId,
  onSelectPage,
}: SlidePanelProps) {
  // ── dnd-kit sensors — calibration copied from Mobile Builder strip ──
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const action = resolveDragEnd(
      event.active.data.current as DragData | undefined,
      event.over?.data.current as DropData | undefined,
    );
    if (!action) return;
    switch (action.type) {
      case 'reorder-fields':
        onReorder(action.from, action.to);
        break;
      case 'move-to-page':
        onMoveFieldToPage(action.fieldId, action.pageId);
        break;
      case 'reorder-pages':
        onReorderPages(action.from, action.to);
        break;
    }
  }, [onReorder, onMoveFieldToPage, onReorderPages]);

  // ── Collapse state ────────────────────────────────────
  const [collapsedPages, setCollapsedPages] = useState<Set<string>>(new Set());

  const toggleCollapse = useCallback((pageId: string) => {
    setCollapsedPages(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  }, []);

  // ── Group fields ──────────────────────────────────────
  const welcomeScreens = useMemo(
    () => fields.filter(f => f.type === 'welcome_screen'),
    [fields]
  );
  const endScreens = useMemo(
    () => fields.filter(f => f.type === 'thank_you_screen' || f.type === 'redirect_url'),
    [fields]
  );

  // Pageable question fields (excludes welcome/end)
  const questions = useMemo(
    () => fields.filter(f => !PAGE_EXCLUDED_FIELD_TYPES.includes(f.type)),
    [fields]
  );

  // Pages sorted by order
  const sortedPages = useMemo(
    () => [...pages].sort((a, b) => a.order - b.order),
    [pages]
  );

  // Fields grouped by pageId
  const fieldsByPage = useMemo(() => {
    const map = new Map<string, FormField[]>();
    for (const page of sortedPages) {
      map.set(page.id, []);
    }
    for (const field of questions) {
      if (field.pageId && map.has(field.pageId)) {
        map.get(field.pageId)!.push(field);
      }
    }
    return map;
  }, [sortedPages, questions]);

  // Ungrouped fields (no pageId or pageId not in pages)
  const ungroupedFields = useMemo(() => {
    const pageIds = new Set(pages.map(p => p.id));
    return questions.filter(f => !f.pageId || !pageIds.has(f.pageId));
  }, [questions, pages]);

  // Get original index in fields array
  const getOriginalIndex = useCallback(
    (field: FormField) => fields.findIndex(f => f.id === field.id),
    [fields]
  );

  // Delete with confirmation
  const handleDelete = (fieldId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este campo?')) {
      onDelete(fieldId);
    }
  };

  // ── Pre-compute question numbering (stable across refactors) ──
  const questionNumberMap = useMemo(() => {
    const map = new Map<string, number>();
    let counter = 0;
    for (const page of sortedPages) {
      const pageFields = fieldsByPage.get(page.id) || [];
      for (const field of pageFields) {
        counter++;
        map.set(field.id, counter);
      }
    }
    for (const field of ungroupedFields) {
      counter++;
      map.set(field.id, counter);
    }
    return map;
  }, [sortedPages, fieldsByPage, ungroupedFields]);

  return (
    <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
      <div className="flex flex-col h-full w-56 min-w-56 bg-neutral-900/60 backdrop-blur-xl border-r border-white/5 overflow-hidden">
        {/* Scrollable slides */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {/* Welcome section */}
          {welcomeScreens.length > 0 && (
            <>
              <SectionSeparator label="BOAS-VINDAS" />
              {welcomeScreens.map(field => (
                <SlideThumbnail
                  key={field.id}
                  field={field}
                  isSelected={selectedId === field.id}
                  onClick={() => onSelect(field.id)}
                  onDelete={() => handleDelete(field.id)}
                  isDraggable={false}
                />
              ))}
            </>
          )}

          {/* Questions section */}
          <SectionSeparator label="PERGUNTAS" />

          {/* Page containers */}
          {sortedPages.map((page, pageIndex) => {
            const pageFields = fieldsByPage.get(page.id) || [];

            return (
              <PageContainer
                key={page.id}
                page={page}
                fields={pageFields}
                pageIndex={pageIndex}
                isSelected={selectedPageId === page.id}
                isCollapsed={collapsedPages.has(page.id)}
                onToggleCollapse={() => toggleCollapse(page.id)}
                onSelect={() => onSelectPage(page.id)}
                onUpdateTitle={(title) => onUpdatePage(page.id, { title })}
                onDelete={() => onDeletePage(page.id)}
              >
                {pageFields.map((field) => (
                  <SlideThumbnail
                    key={field.id}
                    field={field}
                    index={questionNumberMap.get(field.id)}
                    isSelected={selectedId === field.id}
                    onClick={() => onSelect(field.id)}
                    onDuplicate={() => onDuplicate(field.id)}
                    onDelete={() => handleDelete(field.id)}
                    isDraggable
                    originalIndex={getOriginalIndex(field)}
                    compact
                  />
                ))}
              </PageContainer>
            );
          })}

          {/* Ungrouped fields (no pageId) — individual slides */}
          {ungroupedFields.length > 0 && (
            <UngroupedDropZone>
              {ungroupedFields.map(field => (
                <SlideThumbnail
                  key={field.id}
                  field={field}
                  index={questionNumberMap.get(field.id)}
                  isSelected={selectedId === field.id}
                  onClick={() => onSelect(field.id)}
                  onDuplicate={() => onDuplicate(field.id)}
                  onDelete={() => handleDelete(field.id)}
                  isDraggable
                  originalIndex={getOriginalIndex(field)}
                />
              ))}
            </UngroupedDropZone>
          )}

          {/* No questions at all */}
          {questions.length === 0 && sortedPages.length === 0 && (
            <div className="text-center text-neutral-500 text-xs py-6">
              Nenhuma pergunta ainda
            </div>
          )}

          {/* New Page button */}
          <button
            onClick={onAddPage}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-white/10 text-neutral-500 hover:text-neutral-300 hover:border-white/20 hover:bg-white/[0.02] transition-all text-[11px]"
          >
            <Plus className="w-3.5 h-3.5" />
            Nova Página
          </button>

          {/* End screens section (thank you + redirect) */}
          {endScreens.length > 0 && (
            <>
              <SectionSeparator label="ENCERRAMENTO" />
              {endScreens.map(field => (
                <SlideThumbnail
                  key={field.id}
                  field={field}
                  isSelected={selectedId === field.id}
                  onClick={() => onSelect(field.id)}
                  onDelete={() => handleDelete(field.id)}
                  isDraggable={false}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </DndContext>
  );
}

// ─── Ungrouped drop zone (field → no page) ───────────
function UngroupedDropZone({ children }: { children: React.ReactNode }) {
  const { setNodeRef } = useDroppable({
    id: 'ungrouped',
    data: { kind: 'ungrouped' } satisfies DropData,
  });
  return (
    <div ref={setNodeRef} className="space-y-1.5">
      {children}
    </div>
  );
}

// ─── Section Separator ───────────────────────────────
function SectionSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-1">
      <div className="flex-1 h-px bg-white/10" />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 whitespace-nowrap">
        {label}
      </span>
      <div className="flex-1 h-px bg-white/10" />
    </div>
  );
}

// ─── Slide Thumbnail ─────────────────────────────────
interface SlideThumbnailProps {
  field: FormField;
  index?: number;
  isSelected: boolean;
  onClick: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  isDraggable: boolean;
  /** Index in the flat fields array — required when isDraggable. */
  originalIndex?: number;
  compact?: boolean;
}

function SlideThumbnail({
  field,
  index,
  isSelected,
  onClick,
  onDuplicate,
  onDelete,
  isDraggable,
  originalIndex = -1,
  compact,
}: SlideThumbnailProps) {
  const Icon = fieldIconMap[field.type];
  const truncatedLabel = field.label.length > 20 ? field.label.slice(0, 20) + '…' : field.label;

  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: `field-${field.id}`,
    data: { kind: 'field', fieldId: field.id, originalIndex } satisfies DragData,
    disabled: !isDraggable,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `field-target-${field.id}`,
    data: { kind: 'field-target', originalIndex } satisfies DropData,
    disabled: !isDraggable,
  });

  return (
    <div
      ref={(node) => { setDragRef(node); setDropRef(node); }}
      style={transform ? { transform: CSS.Translate.toString(transform), position: 'relative', zIndex: 50 } : undefined}
      className={`group relative rounded-xl cursor-pointer ${isDragging ? '' : 'transition-all'} ${
        isOver ? 'border-t-2 border-primary-400' : ''
      }`}
      onClick={() => { if (!isDragging) onClick(); }}
      {...attributes}
      {...listeners}
    >
      <div
        className={`w-full ${compact ? 'py-1.5 px-2' : 'aspect-video p-2.5'} rounded-xl flex ${compact ? 'items-center gap-2' : 'flex-col justify-between'} transition-all ${
          isSelected
            ? 'bg-white/10 border-2 border-primary-500 shadow-[0_0_12px_rgba(59,130,246,0.15)]'
            : 'bg-neutral-800/50 border border-white/5 hover:bg-neutral-700/50 hover:border-white/10'
        }`}
      >
        {compact ? (
          <>
            {/* Compact: horizontal layout for inside page containers */}
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              {isDraggable && (
                <span className="flex-shrink-0" style={{ touchAction: 'none' }}>
                  <GripVertical className="w-3 h-3 text-neutral-600 opacity-0 group-hover:opacity-100 pointer-coarse:opacity-100 transition-opacity cursor-grab" />
                </span>
              )}
              {Icon && <Icon className="w-3 h-3 text-neutral-400 flex-shrink-0" />}
              {index !== undefined && (
                <span className="text-[10px] font-semibold text-neutral-500">{index}</span>
              )}
              <p className="text-[10px] text-neutral-300 leading-tight truncate">
                {truncatedLabel}
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Full: original card layout */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {Icon && <Icon className="w-3.5 h-3.5 text-neutral-400" />}
                {index !== undefined && (
                  <span className="text-[11px] font-semibold text-neutral-400">{index}</span>
                )}
              </div>
              {isDraggable && (
                <span style={{ touchAction: 'none' }}>
                  <GripVertical className="w-3 h-3 text-neutral-600 opacity-0 group-hover:opacity-100 pointer-coarse:opacity-100 transition-opacity cursor-grab" />
                </span>
              )}
            </div>
            <p className="text-[11px] text-neutral-300 leading-tight truncate mt-auto">
              {truncatedLabel}
            </p>
          </>
        )}
      </div>

      {/* Action buttons — hover on fine pointers, always visible on coarse (D3) */}
      {(onDuplicate || onDelete) && (
        <div
          className="absolute top-1 right-1 gap-0.5 z-10 hidden group-hover:flex pointer-coarse:flex"
          onPointerDown={e => e.stopPropagation()}
        >
          {onDuplicate && (
            <button
              onClick={e => { e.stopPropagation(); onDuplicate(); }}
              className="p-1 rounded-md bg-neutral-800/90 text-neutral-400 hover:text-white hover:bg-neutral-700 transition-colors"
              title="Duplicar"
            >
              <Copy className="w-3 h-3" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="p-1 rounded-md bg-neutral-800/90 text-neutral-400 hover:text-red-400 hover:bg-neutral-700 transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
